// src/components/SimilarProducts.jsx

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchSimilarProducts } from "../../redux/actions/similarProductsActions";
import { FaRegHeart, FaCartPlus } from "react-icons/fa";
import * as other from "../../components/other";
import { addToCartThunk } from "../../redux/actions/cartActions";
import {
  toggleWishlistItem,
  fetchWishlist,
} from "../../redux/actions/wishlistAction";
import { useNavigate } from "react-router-dom";
import { checkStockAvailability } from "../../redux/actions/stockActions";
import { toast } from "react-toastify";
import * as Alert from "../common/Alert";

const SimilarProducts = ({ pro_id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, products, error } = useSelector(
    (state) => state.similarProduct
  );
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const userType = useSelector((state) => state.auth.user?.user?.user_type);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subProducts, setSubProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);

  // fetch similiar + wishlist on mount
  useEffect(() => {
    if (pro_id) dispatch(fetchSimilarProducts(pro_id));
    dispatch(fetchWishlist());
  }, [dispatch, pro_id]);

  // quantity controls
  const incrementQuantity = (sub) => {
    const sub_product_id = sub.sub_pro_id;
    const quantity = (quantities[sub_product_id] || 1) + 1;

    // First, log what we're sending

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          toast.info("Insufficient Stock !!!");
        } else {
          // Stock is available, increment quantity
          setQuantities((prev) => ({
            ...prev,
            [sub_product_id]: quantity,
          }));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const decrementQuantity = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  // open sub-product modal
  const handleAddToCartClick = (e, item) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      return navigate("/login");
    }
    if (item?.sub_products?.length) {
      setSubProducts(item.sub_products);
      setSelectedProduct(item.product);
      // init quantities
      const init = {};
      item.sub_products.forEach((s) => {
        init[s.sub_pro_id] = 1;
      });
      setQuantities(init);
      setIsModalOpen(true);
    } else {
    }
  };

  // add sub-product to cart
  const handleAddSubProductToCart = (sub) => {
    const product_id = selectedProduct.pro_id;
    const sub_product_id = sub.sub_pro_id;
    const quantity = quantities[sub_product_id] || 1;

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          Alert.Alert.ErrorAlert("Insufficient stock!");
        } else {
          // Stock is available, increment quantity
          dispatch(
            addToCartThunk({
              product_id: product_id,
              sub_product_id,
              quantity,
            })
          );
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  // redirection
  const handleQuickViewClick = (product) => {
    setSelectedProduct(product);
    navigate(`/ProductsDetails/${product.pro_id}`);
  };

  // wishlist toggle
  const handleWishlist = (e, productId) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      return navigate("/login");
    }
    dispatch(toggleWishlistItem(productId));
  };

  if (loading) return <p>Loading similar products...</p>;
  if (error) return <p className="text-red-500">Error: {error}</p>;
  if (!Array.isArray(products) || products.length === 0)
    return <p>No similar products found.</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3">
        <div className="border-l-4 border-medium-theme-color" />
        <h5 className="font-semibold text-xl lg:text-2xl">Similar Products</h5>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {products.map((item, idx) => {
          const product = item.product;
          const isWishlisted = wishlistItems.some(
            (w) => (w.product?.pro_id || w.pro_id) === product.pro_id
          );
          return (
            <div
              key={product.pro_id || idx}
              className="cursor-pointer "
              onClick={(e) => handleQuickViewClick(product)}
            >
              <div className="lg:px-2 border-0 group relative">
                <div className="aspect-[3/4] overflow-hidden rounded-3xl">
                  <img
                    loading="lazy"
                    src={product.product_primary_img}
                    alt={product.product_name}
                    className="object-fit size-full"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://via.placeholder.com/150?text=No+Image";
                    }}
                  />
                </div>
                {/* <div className="home-section-quick-view-btn absolute bottom-8 left-1/2 transform -translate-x-1/2">
                  <button
                    className="btn bg-medium-theme-color text-white hover:bg-medium-theme-color rounded-pill"
                    onClick={() => handleQuickViewClick(product)}
                  >
                    Quick View
                  </button>
                </div> */}

                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-truncate pb-0 fs-5 fs-6">
                      {product.product_name}
                    </span>
                    <small className="text-truncate pt-0 fw-bold -mb-2">
                      {product.product_sub_title || "Category"}
                    </small>
                  </div>
                  {isAuthenticated && (
                    <div className="d-flex align-items-center justify-content-end">
                      <span className="px-2 py-1 text-base font-semibold rounded-xl bg-white">
                        ₹
                        {userType === "vendor"
                          ? item.sub_products?.[0]?.vendor_product_price
                          : item.sub_products?.[0]?.customer_product_price ||
                            "-"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="position-absolute top-[3%] right-[3%] d-flex flex-column gap-2 group-hover:-translate-y-5">
                  <button
                    onClick={(e) => handleWishlist(e, product.pro_id)}
                    className={`badge rounded-circle p-2 fs-6 transition-colors ${
                      isWishlisted
                        ? "bg-red-500 text-white"
                        : "bg-tealish text-white"
                    }`}
                    title={
                      isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"
                    }
                  >
                    <FaRegHeart />
                  </button>

                  <button
                    onClick={(e) => handleAddToCartClick(e, item)}
                    className="badge rounded-circle bg-tealish text-white p-2 fs-6"
                  >
                    <FaCartPlus />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <other.Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Select Sub Product"
        >
          <div className="flex flex-col gap-4">
            {subProducts.map((sub, idx) => (
              <div
                key={idx}
                className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
              >
                {/* Image on the left */}
                <img
                  loading="lazy"
                  src={sub.sub_pro_img}
                  alt={`sub-product-${idx}`}
                  className="w-32 h-44 object-cover hover:scale-110 transition-transform ease-in-out duration-500 cursor-pointer rounded-lg flex-shrink-0"
                />

                {/* Details on the right */}
                <div className="flex flex-col justify-between flex-grow h-full">
                  <div className="space-y-1">
                    <p>
                      <strong>Color:</strong> {sub.color_id}
                    </p>
                    <p>
                      <strong>Size:</strong> {sub.size}
                    </p>
                    {/* <p><strong>Location:</strong> {sub.location}</p> */}
                    {isAuthenticated ? (
                      <p>
                        <strong>Price:</strong> ₹
                        {userType === "vendor"
                          ? sub.set_price
                          : sub.customer_product_price}
                      </p>
                    ) : (
                      ""
                    )}
                  </div>

                  {/* Quantity controls */}
                  <div className="self-start flex items-center mt-2 gap-2 ">
                    <div className="border border-gray-300 rounded-full overflow-hidden">
                      <button
                        onClick={() => decrementQuantity(sub.sub_pro_id)}
                        className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                        disabled={quantities[sub.sub_pro_id] <= 1}
                      >
                        -
                      </button>
                      <span className="px-4 py-1 select-none">
                        {quantities[sub.sub_pro_id] || 1}
                      </span>
                      <button
                        onClick={() => incrementQuantity(sub)}
                        className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      >
                        +
                      </button>
                    </div>

                    {userType === "vendor" ? (
                      sub.selling_type == "set" ? (
                        <span className="ml-2">
                          {quantities[sub.sub_pro_id] <= 1 ? "Set" : "Sets"}{" "}
                        </span>
                      ) : (
                        <span className="ml-2">
                          {quantities[sub.sub_pro_id] <= 1 ? "Piece" : "Pieces"}
                        </span>
                      )
                    ) : null}
                  </div>

                  <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                    {/* Note Text */}
                    {userType === "vendor" &&
                    sub.selling_type == "set" &&
                    sub.no_of_pcs ? (
                      <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
                        {` ${quantities[sub.sub_pro_id] || 1} Set = ${
                          sub.no_of_pcs * (quantities[sub.sub_pro_id] || 1)
                        } pieces`}{" "}
                        (₹{sub.set_price * (quantities[sub?.sub_pro_id] || 1)})
                      </p>
                    ) : (
                      // Invisible div to take up the same space so button stays right-aligned
                      <div className="min-w-[180px]" />
                    )}

                    {/* Add Button */}
                    {isAuthenticated ? (
                      <div className="flex flex-col gap-1 w-full sm:w-fit">
                        <button
                          className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleAddSubProductToCart(sub)}
                          disabled={sub.no_of_stock <= 0}
                        >
                          Add Item
                        </button>

                        {sub.no_of_stock <= 0 && (
                          <p className="text-red-600 text-sm font-semibold">
                            Out of Stock
                          </p>
                        )}
                      </div>
                    ) : (
                      navigate("/login")
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </other.Modal>
      )}
    </div>
  );
};

export default SimilarProducts;
